/**
 * Pictogram catalogue for the notice generator.
 *
 * Every icon is authored as plain SVG markup in a 0..100 box so the same source
 * can be shown in the picker (inline SVG) and rasterised for the export (SVG
 * data URL → <img> → drawImage). Nothing here contains <text>, because an SVG
 * loaded through an <img> gets no web fonts — glyphs are geometry only.
 *
 * Tokens inside the markup are swapped at render time:
 *   %C% — the icon's ink colour
 *   %H% — the "hole" colour (the notice background), for knockouts such as a
 *         camera lens or the bar inside a warning triangle
 *   %R% — the prohibition ring colour
 *
 * `keywords` drives the auto-pick: the tool scores the notice text against
 * these fragments (Bangla first, English second) and selects the best match, so
 * typing "ধূমপান নিষেধ" lands on the no-smoking pictogram without a click.
 */

/** Ring styles a pictogram can carry. */
const RING_SLASH = 'slash'; // red circle + diagonal bar (prohibition)
const RING_NONE = 'none';

/* ------------------------------------------------------------------ *
 *  Icon definitions
 * ------------------------------------------------------------------ */

export const NOTICE_ICONS = [
    {
        id: 'noSmoking',
        ring: RING_SLASH,
        keywords: ['ধুমপান', 'ধূমপান', 'সিগারেট', 'বিড়ি', 'তামাক', 'smok', 'cigarette', 'tobacco', 'vape'],
        glyph: `
            <rect x="10" y="52" width="58" height="15" rx="3" fill="%C%"/>
            <rect x="73" y="52" width="17" height="15" rx="3" fill="%C%"/>
            <path d="M30 40c7-5 7-13 0-18M50 40c7-5 7-13 0-18M70 40c7-5 7-13 0-18"
                  fill="none" stroke="%C%" stroke-width="6" stroke-linecap="round"/>
        `,
    },
    {
        id: 'noPhoto',
        ring: RING_SLASH,
        keywords: ['ছবি তোলা', 'ছবি', 'ফটো', 'ক্যামেরা', 'photo', 'camera', 'picture', 'photograph'],
        glyph: `
            <path d="M8 32h18l8-10h32l8 10h18a7 7 0 0 1 7 7v37a7 7 0 0 1-7 7H8a7 7 0 0 1-7-7V39a7 7 0 0 1 7-7Z" fill="%C%"/>
            <circle cx="50" cy="58" r="16" fill="%H%"/>
            <circle cx="50" cy="58" r="8" fill="%C%"/>
        `,
    },
    {
        id: 'noShoes',
        ring: RING_SLASH,
        keywords: ['জুতা', 'জুতো', 'স্যান্ডেল', 'পাদুকা', 'খুলে', 'shoe', 'footwear', 'sandal', 'barefoot'],
        glyph: `
            <path d="M30 92c-8 0-12-5-12-12 0-9 4-14 4-24 0-9-3-14-3-22 0-8 5-13 13-13s13 6 13 15c0 14 8 20 8 33 0 15-9 23-23 23Z" fill="%C%"/>
            <circle cx="70" cy="26" r="9" fill="%C%"/>
            <circle cx="84" cy="40" r="7" fill="%C%"/>
            <circle cx="86" cy="58" r="6" fill="%C%"/>
        `,
    },
    {
        id: 'noMobile',
        ring: RING_SLASH,
        keywords: ['মোবাইল', 'ফোন', 'সেলফোন', 'বন্ধ রাখুন', 'mobile', 'phone', 'cell', 'device'],
        glyph: `
            <rect x="28" y="12" width="44" height="76" rx="8" fill="%C%"/>
            <rect x="35" y="23" width="30" height="48" rx="2" fill="%H%"/>
            <circle cx="50" cy="80" r="5" fill="%H%"/>
        `,
    },
    {
        id: 'cctv',
        ring: RING_NONE,
        keywords: ['সিসি', 'ক্যামেরার নজরদারি', 'নজরদারি', 'সিসিটিভি', 'নিরাপত্তা', 'cctv', 'surveillance', 'security camera', 'monitored'],
        glyph: `
            <path d="M6 30 66 14l7 25L13 55Z" fill="%C%"/>
            <path d="M73 22 92 17l6 22-19 5Z" fill="%C%"/>
            <path d="M30 48v10M18 62h34" fill="none" stroke="%C%" stroke-width="8" stroke-linecap="round"/>
            <path d="M35 62 24 92M35 62l11 30" fill="none" stroke="%C%" stroke-width="8" stroke-linecap="round"/>
        `,
    },
    {
        id: 'saveElectricity',
        ring: RING_SLASH,
        keywords: ['বিদ্যুৎ', 'বিদ্যুত', 'অপচয়', 'লাইট', 'বাতি', 'electric', 'power waste', 'light', 'bulb', 'energy'],
        glyph: `
            <path d="M50 6a28 28 0 0 0-17 50c4 3 5 7 5 11v3h24v-3c0-4 1-8 5-11A28 28 0 0 0 50 6Z" fill="%C%"/>
            <rect x="37" y="76" width="26" height="8" rx="4" fill="%C%"/>
            <rect x="40" y="88" width="20" height="8" rx="4" fill="%C%"/>
        `,
    },
    {
        id: 'noEntry',
        ring: RING_SLASH,
        keywords: ['প্রবেশ নিষেধ', 'প্রবেশ করা', 'ঢোকা', 'অনুপ্রবেশ', 'no entry', 'do not enter', 'restricted', 'no admission'],
        glyph: `
            <rect x="8" y="42" width="84" height="17" rx="5" fill="%C%"/>
        `,
    },
    {
        id: 'noParking',
        ring: RING_SLASH,
        keywords: ['পার্কিং', 'গাড়ি রাখা', 'parking', 'no parking'],
        glyph: `
            <path d="M28 8h28a24 24 0 0 1 0 48H45v36H28Z" fill="%C%"/>
            <path d="M45 22h11a10 10 0 0 1 0 20H45Z" fill="%H%"/>
        `,
    },
    {
        id: 'noLittering',
        ring: RING_NONE,
        keywords: ['ময়লা', 'আবর্জনা', 'ডাস্টবিন', 'পরিষ্কার', 'নোংরা', 'litter', 'trash', 'garbage', 'waste', 'clean'],
        glyph: `
            <rect x="18" y="20" width="64" height="11" rx="5" fill="%C%"/>
            <rect x="40" y="8" width="20" height="9" rx="4" fill="%C%"/>
            <path d="M25 36h50l-5 52a8 8 0 0 1-8 7H38a8 8 0 0 1-8-7Z" fill="%C%"/>
            <path d="M42 48v34M58 48v34" fill="none" stroke="%H%" stroke-width="6" stroke-linecap="round"/>
        `,
    },
    {
        id: 'noHorn',
        ring: RING_SLASH,
        keywords: ['হর্ন', 'হাইড্রলিক', 'শব্দ', 'আওয়াজ', 'horn', 'honk', 'noise'],
        glyph: `
            <path d="M8 38h16L46 18v64L24 62H8Z" fill="%C%"/>
            <path d="M60 34a20 20 0 0 1 0 32M74 22a36 36 0 0 1 0 56"
                  fill="none" stroke="%C%" stroke-width="8" stroke-linecap="round"/>
        `,
    },
    {
        id: 'silence',
        ring: RING_SLASH,
        keywords: ['নীরবতা', 'নিরবতা', 'চুপ', 'কথা বলা', 'silence', 'quiet', 'silent'],
        glyph: `
            <path d="M14 38h18L58 16v68L32 62H14Z" fill="%C%"/>
            <path d="M70 38 92 62M92 38 70 62" fill="none" stroke="%C%" stroke-width="9" stroke-linecap="round"/>
        `,
    },
    {
        id: 'maskRequired',
        ring: RING_NONE,
        keywords: ['মাস্ক', 'মুখোশ', 'mask', 'face mask'],
        glyph: `
            <path d="M18 34c14-7 50-7 64 0l10 4v10l-10 3c-2 18-14 31-32 31S22 69 20 51l-10-3V38Z" fill="%C%"/>
            <path d="M28 46h44M28 60h44" fill="none" stroke="%H%" stroke-width="6" stroke-linecap="round"/>
        `,
    },
    {
        id: 'saveWater',
        ring: RING_SLASH,
        keywords: ['পানি', 'জল', 'পানির অপচয়', 'water', 'save water'],
        glyph: `
            <path d="M50 6c0 0 30 34 30 52a30 30 0 0 1-60 0C20 40 50 6 50 6Z" fill="%C%"/>
            <path d="M34 62a16 16 0 0 0 12 16" fill="none" stroke="%H%" stroke-width="6" stroke-linecap="round"/>
        `,
    },
    {
        id: 'noFood',
        ring: RING_SLASH,
        keywords: ['খাবার', 'খাওয়া', 'পানীয়', 'food', 'eating', 'drink', 'no food'],
        glyph: `
            <path d="M18 6v26a10 10 0 0 0 20 0V6" fill="none" stroke="%C%" stroke-width="8" stroke-linecap="round"/>
            <path d="M28 32v62" fill="none" stroke="%C%" stroke-width="9" stroke-linecap="round"/>
            <path d="M72 6c-9 0-14 10-14 24s5 18 10 18v46" fill="none" stroke="%C%" stroke-width="9" stroke-linecap="round" stroke-linejoin="round"/>
        `,
    },
    {
        id: 'noPets',
        ring: RING_SLASH,
        keywords: ['কুকুর', 'বিড়াল', 'পোষা', 'প্রাণী', 'pet', 'dog', 'animal'],
        glyph: `
            <ellipse cx="26" cy="34" rx="11" ry="14" fill="%C%"/>
            <ellipse cx="50" cy="24" rx="11" ry="15" fill="%C%"/>
            <ellipse cx="74" cy="34" rx="11" ry="14" fill="%C%"/>
            <path d="M50 46c16 0 26 12 26 24 0 12-10 18-26 18S24 82 24 70c0-12 10-24 26-24Z" fill="%C%"/>
        `,
    },
    {
        id: 'danger',
        ring: RING_NONE,
        keywords: ['বিপদ', 'সতর্ক', 'সাবধান', 'ঝুঁকি', 'danger', 'warning', 'caution', 'hazard'],
        glyph: `
            <path d="M50 6 97 92H3Z" fill="%C%"/>
            <rect x="44" y="38" width="12" height="30" rx="6" fill="%H%"/>
            <circle cx="50" cy="78" r="7" fill="%H%"/>
        `,
    },
    {
        id: 'wetFloor',
        ring: RING_NONE,
        keywords: ['ভেজা', 'পিচ্ছিল', 'মেঝে', 'wet floor', 'slippery', 'caution wet'],
        glyph: `
            <path d="M50 4 96 84H4Z" fill="%C%"/>
            <path d="M50 40c0 0 12 14 12 22a12 12 0 0 1-24 0c0-8 12-22 12-22Z" fill="%H%"/>
            <path d="M6 94h88" fill="none" stroke="%C%" stroke-width="8" stroke-linecap="round"/>
        `,
    },
    {
        id: 'firstAid',
        ring: RING_NONE,
        keywords: ['প্রাথমিক চিকিৎসা', 'চিকিৎসা', 'মেডিকেল', 'first aid', 'medical', 'clinic'],
        glyph: `
            <rect x="6" y="16" width="88" height="68" rx="12" fill="%C%"/>
            <path d="M42 30h16v14h14v16H58v14H42V60H28V44h14Z" fill="%H%"/>
        `,
    },
    {
        id: 'exit',
        ring: RING_NONE,
        keywords: ['বাহির', 'বের হওয়া', 'বহির্গমন', 'জরুরি', 'exit', 'emergency exit', 'way out'],
        glyph: `
            <path d="M14 10h36v80H14Z" fill="%C%"/>
            <circle cx="41" cy="52" r="4.5" fill="%H%"/>
            <path d="M60 50h30M78 38l12 12-12 12" fill="none" stroke="%C%" stroke-width="9" stroke-linecap="round" stroke-linejoin="round"/>
        `,
    },
    {
        id: 'powerOff',
        ring: RING_NONE,
        keywords: ['সুইচ', 'বন্ধ করুন', 'পাওয়ার', 'switch off', 'turn off', 'power'],
        glyph: `
            <path d="M50 8v40" fill="none" stroke="%C%" stroke-width="11" stroke-linecap="round"/>
            <path d="M26 24a34 34 0 1 0 48 0" fill="none" stroke="%C%" stroke-width="11" stroke-linecap="round"/>
        `,
    },
    {
        id: 'prayer',
        ring: RING_NONE,
        keywords: ['নামাজ', 'নামায', 'মসজিদ', 'সালাত', 'আজান', 'prayer', 'mosque', 'salah'],
        glyph: `
            <path d="M50 6c-16 12-25 25-25 38v10h50V44c0-13-9-26-25-38Z" fill="%C%"/>
            <path d="M20 58h60v34H20Z" fill="%C%"/>
            <path d="M50 92V74a9 9 0 0 1 18 0v18Z" fill="%H%"/>
            <rect x="4" y="30" width="10" height="62" rx="5" fill="%C%"/>
            <rect x="86" y="30" width="10" height="62" rx="5" fill="%C%"/>
        `,
    },
    {
        id: 'noVehicle',
        ring: RING_SLASH,
        keywords: ['গাড়ি', 'যানবাহন', 'রিকশা', 'মোটরসাইকেল', 'vehicle', 'car', 'no vehicle'],
        glyph: `
            <path d="M10 58 20 32h60l10 26v22H78a9 9 0 0 1-18 0H40a9 9 0 0 1-18 0H10Z" fill="%C%"/>
            <path d="M26 40h48l5 14H21Z" fill="%H%"/>
        `,
    },
    {
        id: 'noCrowd',
        ring: RING_SLASH,
        keywords: ['কোলাহল', 'ভিড়', 'জমায়েত', 'crowd', 'gathering'],
        glyph: `
            <circle cx="34" cy="28" r="15" fill="%C%"/>
            <circle cx="70" cy="34" r="12" fill="%C%"/>
            <path d="M6 92c0-17 13-30 28-30s28 13 28 30Z" fill="%C%"/>
            <path d="M62 92c0-14 8-24 20-24s16 10 16 24Z" fill="%C%"/>
        `,
    },
    {
        id: 'idCard',
        ring: RING_NONE,
        keywords: ['পরিচয়পত্র', 'আইডি', 'কার্ড', 'অনুমতি', 'id card', 'identity', 'badge', 'pass'],
        glyph: `
            <rect x="6" y="18" width="88" height="64" rx="10" fill="%C%"/>
            <circle cx="32" cy="44" r="10" fill="%H%"/>
            <path d="M16 70c0-9 7-15 16-15s16 6 16 15Z" fill="%H%"/>
            <path d="M58 40h26M58 54h26M58 66h18" fill="none" stroke="%H%" stroke-width="7" stroke-linecap="round"/>
        `,
    },
    {
        id: 'helmetRequired',
        ring: RING_NONE,
        keywords: ['হেলমেট', 'শিরস্ত্রাণ', 'helmet', 'hard hat'],
        glyph: `
            <path d="M10 64a40 40 0 0 1 80 0v3H10Z" fill="%C%"/>
            <rect x="4" y="67" width="92" height="14" rx="7" fill="%C%"/>
            <path d="M62 28c14 7 22 20 24 34" fill="none" stroke="%H%" stroke-width="6" stroke-linecap="round"/>
        `,
    },
    {
        id: 'noAlcohol',
        ring: RING_SLASH,
        keywords: ['মদ', 'মদ্যপান', 'অ্যালকোহল', 'alcohol', 'liquor', 'drinking'],
        glyph: `
            <path d="M32 4h18v16c0 6 8 11 8 21v49a8 8 0 0 1-8 8H32a8 8 0 0 1-8-8V41c0-10 8-15 8-21Z" fill="%C%"/>
            <rect x="24" y="48" width="34" height="12" fill="%H%"/>
            <path d="M64 28h32l-7 26-6 5v27h9v8H68v-8h9V59l-6-5z" fill="%C%"/>
        `,
    },
    {
        id: 'noBicycle',
        ring: RING_SLASH,
        keywords: ['সাইকেল', 'বাইসাইকেল', 'bicycle', 'cycle', 'bike'],
        glyph: `
            <g fill="none" stroke="%C%" stroke-width="7" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="22" cy="70" r="19"/>
                <circle cx="78" cy="70" r="19"/>
                <path d="M22 70 46 34h20l12 36M46 34H34M54 70H34"/>
            </g>
            <circle cx="70" cy="20" r="7" fill="%C%"/>
        `,
    },
    {
        id: 'wifi',
        ring: RING_NONE,
        keywords: ['ওয়াইফাই', 'ইন্টারনেট', 'wifi', 'internet', 'network'],
        glyph: `
            <g fill="none" stroke="%C%" stroke-width="10" stroke-linecap="round">
                <path d="M8 36a60 60 0 0 1 84 0"/>
                <path d="M25 55a36 36 0 0 1 50 0"/>
                <path d="M40 72a16 16 0 0 1 20 0"/>
            </g>
            <circle cx="50" cy="88" r="7" fill="%C%"/>
        `,
    },
    {
        id: 'toilet',
        ring: RING_NONE,
        keywords: ['টয়লেট', 'ওয়াশরুম', 'বাথরুম', 'শৌচাগার', 'toilet', 'washroom', 'restroom'],
        glyph: `
            <circle cx="27" cy="14" r="10" fill="%C%"/>
            <path d="M18 28h18l8 32h-9v36H23V60h-9z" fill="%C%"/>
            <circle cx="73" cy="14" r="10" fill="%C%"/>
            <path d="M73 27c9 0 15 6 17 17l4 20h-10l-2 32H64l-2-32H52l4-20c2-11 8-17 17-17Z" fill="%C%"/>
        `,
    },
    {
        id: 'noTouch',
        ring: RING_SLASH,
        keywords: ['হাত দেবেন না', 'স্পর্শ', 'ছোঁবেন না', 'touch', 'do not touch'],
        // A hand reads far better as separate palm + fingers than as one
        // outline path — an outline at this size collapses into a blob.
        glyph: `
            <rect x="26" y="46" width="52" height="48" rx="18" fill="%C%"/>
            <rect x="30" y="20" width="11" height="42" rx="5.5" fill="%C%"/>
            <rect x="44" y="12" width="11" height="50" rx="5.5" fill="%C%"/>
            <rect x="58" y="16" width="11" height="46" rx="5.5" fill="%C%"/>
            <rect x="71" y="26" width="11" height="36" rx="5.5" fill="%C%"/>
            <rect x="8" y="46" width="28" height="11" rx="5.5" fill="%C%" transform="rotate(-28 22 51)"/>
        `,
    },
    {
        id: 'sanitize',
        ring: RING_NONE,
        keywords: ['হাত ধুয়ে', 'স্যানিটাইজ', 'জীবাণুমুক্ত', 'sanitize', 'wash hands', 'hand wash'],
        glyph: `
            <g transform="translate(14 8) scale(0.86)">
                <rect x="26" y="46" width="52" height="48" rx="18" fill="%C%"/>
                <rect x="30" y="20" width="11" height="42" rx="5.5" fill="%C%"/>
                <rect x="44" y="12" width="11" height="50" rx="5.5" fill="%C%"/>
                <rect x="58" y="16" width="11" height="46" rx="5.5" fill="%C%"/>
                <rect x="71" y="26" width="11" height="36" rx="5.5" fill="%C%"/>
            </g>
            <path d="M16 6c0 0 11 13 11 20a11 11 0 0 1-22 0c0-7 11-20 11-20Z" fill="%C%"/>
        `,
    },
    {
        id: 'arrow',
        ring: RING_NONE,
        keywords: ['এদিকে', 'তীর', 'দিক', 'arrow', 'this way', 'direction'],
        glyph: `
            <g fill="none" stroke="%C%" stroke-width="11" stroke-linecap="round" stroke-linejoin="round">
                <path d="M8 50h74"/>
                <path d="M58 26 82 50 58 74"/>
            </g>
        `,
    },
    {
        id: 'stairs',
        ring: RING_NONE,
        keywords: ['সিঁড়ি', 'সিড়ি', 'stairs', 'staircase', 'steps'],
        glyph: `
            <path d="M6 92h22V70h22V48h22V26h22" fill="none" stroke="%C%" stroke-width="9"
                  stroke-linecap="round" stroke-linejoin="round"/>
        `,
    },
    {
        id: 'noPlastic',
        ring: RING_SLASH,
        keywords: ['প্লাস্টিক', 'পলিথিন', 'বোতল', 'plastic', 'polythene', 'bottle'],
        glyph: `
            <path d="M40 4h20v11c0 5 11 9 11 21v50a10 10 0 0 1-10 10H39a10 10 0 0 1-10-10V36c0-12 11-16 11-21Z" fill="%C%"/>
            <rect x="29" y="44" width="42" height="11" fill="%H%"/>
        `,
    },
    {
        id: 'fireExtinguisher',
        ring: RING_NONE,
        keywords: ['অগ্নিনির্বাপক', 'আগুন', 'ফায়ার', 'extinguisher', 'fire'],
        glyph: `
            <rect x="30" y="30" width="40" height="66" rx="13" fill="%C%"/>
            <rect x="38" y="12" width="15" height="19" rx="5" fill="%C%"/>
            <path d="M54 18h22l7 9" fill="none" stroke="%C%" stroke-width="7" stroke-linecap="round" stroke-linejoin="round"/>
            <rect x="37" y="50" width="26" height="18" fill="%H%"/>
        `,
    },
    {
        id: 'officeHours',
        ring: RING_NONE,
        keywords: ['অফিস সময়', 'সময়সূচি', 'খোলা', 'বন্ধের সময়', 'office hours', 'timing', 'schedule'],
        glyph: `
            <circle cx="50" cy="50" r="42" fill="none" stroke="%C%" stroke-width="9"/>
            <path d="M50 24v28l20 12" fill="none" stroke="%C%" stroke-width="9"
                  stroke-linecap="round" stroke-linejoin="round"/>
        `,
    },
];

const ICON_BY_ID = new Map(NOTICE_ICONS.map((icon) => [icon.id, icon]));

export function getIconById(id) {
    return ICON_BY_ID.get(id) || null;
}

/* ------------------------------------------------------------------ *
 *  Keyword → icon
 * ------------------------------------------------------------------ */

/**
 * Best-matching pictogram for a notice text, or null when nothing matches.
 *
 * A longer keyword beats a shorter one ("ছবি তোলা" over "ছবি") so the more
 * specific sign wins, and English matches only on word-ish boundaries to keep
 * "car" out of "carry".
 */
export function detectIconId(text) {
    const raw = String(text || '');
    if (!raw.trim()) return null;

    const haystack = raw.toLowerCase();
    let bestId = null;
    let bestScore = 0;

    for (const icon of NOTICE_ICONS) {
        for (const keyword of icon.keywords) {
            const needle = keyword.toLowerCase();
            const index = haystack.indexOf(needle);
            if (index < 0) continue;

            // Latin keywords must not fire from inside a longer word.
            if (/^[a-z ]+$/.test(needle)) {
                const before = haystack[index - 1];
                const after = haystack[index + needle.length];
                if ((before && /[a-z]/.test(before)) || (after && /[a-z]/.test(after))) continue;
            }

            if (needle.length > bestScore) {
                bestScore = needle.length;
                bestId = icon.id;
            }
        }
    }

    return bestId;
}

/* ------------------------------------------------------------------ *
 *  Rendering
 * ------------------------------------------------------------------ */

function fillTokens(markup, { color, hole, ring }) {
    return markup
        .replace(/%C%/g, color)
        .replace(/%H%/g, hole)
        .replace(/%R%/g, ring);
}

/**
 * Full SVG document for one pictogram.
 * @param {string} id icon id
 * @param {{color?: string, hole?: string, ringColor?: string}} opts
 * @returns {string|null} SVG markup, or null for an unknown id
 */
export function buildIconSvg(id, { color = '#111111', hole = '#ffffff', ringColor = '#D92B2B' } = {}) {
    const icon = getIconById(id);
    if (!icon) return null;

    const glyph = fillTokens(icon.glyph, { color, hole, ring: ringColor });

    // Inside a prohibition ring the glyph is shrunk about the centre so it
    // clears the stroke; without a ring it uses the whole box.
    const body = icon.ring === RING_SLASH
        ? `
            <g transform="translate(50 50) scale(0.66) translate(-50 -50)">${glyph}</g>
            <circle cx="50" cy="50" r="42.5" fill="none" stroke="${ringColor}" stroke-width="10"/>
            <rect x="44.5" y="8" width="11" height="84" fill="${ringColor}" transform="rotate(45 50 50)"/>
        `
        : glyph;

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100">${body}</svg>`;
}

/** `buildIconSvg` output as a data URL, ready for an <img> or drawImage. */
export function iconSvgDataUrl(id, opts) {
    const svg = buildIconSvg(id, opts);
    return svg ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}` : null;
}
